import {
  QueryFetchType,
  UseInfiniteQueryReturnProps,
  UsePaginatedQueryReturnProps,
  UseQueryProps,
  UseQueryReturnType,
} from './props';
export * from './props';

/**
 * Hook to manage either a paginated or infinite query based on the provided type.
 *
 * @template T - The query type ('infinity' or 'paginated').
 * @template D - The data type returned by the query.
 * @template K - The query key type.
 *
 * @param {UseQueryProps<T>} options - Options to configure the query.
 * @param {function(QueryProps): UseInfiniteQueryReturnProps<D, K>} useInfinityQuery - Hook for infinite query.
 * @param {function(QueryProps): UsePaginatedQueryReturnProps<D, K>} usePaginatedQuery - Hook for paginated query.
 *
 * @returns {UseQueryReturnType<T, D, K>} - The query result based on the type.
 */
export function usePaginatedOrInfiniteQuery<T extends QueryFetchType, D>(
  { type, enabled = true, ...rest }: UseQueryProps<T, any>,
  useInfinityQuery: (
    props: Omit<UseQueryProps<T, any>, 'type' | 'enabled'>,
  ) => UseInfiniteQueryReturnProps<D>,
  usePaginatedQuery: (
    props: Omit<UseQueryProps<T, any>, 'type' | 'enabled'>,
  ) => UsePaginatedQueryReturnProps<D>,
): UseQueryReturnType<T, D> {
  const infiniteData = useInfinityQuery({
    enabled: type === 'infinity' && enabled,
    type,
    ...rest,
  });
  const paginatedData = usePaginatedQuery({
    type,
    enabled: type === 'paginated' && enabled,
    ...rest,
  });

  if (type === 'infinity') {
    return infiniteData as unknown as UseQueryReturnType<T, D>;
  }

  return paginatedData as unknown as UseQueryReturnType<T, D>;
}
